<template>
  <dictionary-base
    v-model="localModel"
    v-model:menu="menu"
    v-model:search="query"
    :auto-select-first="false"
    :class="bindClass(schema) + requiredInputClass"
    :hide-no-data="false"
    :items="data"
    :label="label"
    :lazy="true"
    :loading="loading"
    :max-selection="maxSelection"
    :multiple="multiple"
    :no-data-text="t('userInput.noData')"
    :options="paginationOptions"
    :rules="activeRules"
    component="v-autocomplete"
    item-title="firstName"
    item-value="id"
    no-filter
    return-object
    v-bind="fieldProps"
    @click="fetchData($event)"
    @loadMoreRecords="loadMoreRecords"
    @update:model-value="valueHasChanged"
  >
    <template #selection="{ item }">
      <v-chip
        :closable="!fieldProps.readonly"
        close-icon="mdi-close"
        label
        variant="outlined"
        @click:close="removeValue(item.raw)"
      >
        <span>
          {{ item.raw.name }}
        </span>
      </v-chip>
    </template>

    <template #item="{ item, props: p }">
      <v-list-item
        class="px-3"
        v-bind="{ ...p, title: '' }"
        @click="menu = false"
      >
        <v-list-item-title>
          <v-row
            align="center"
            dense
          >
            <v-col cols="auto">
              <avatar-provider
                :group-mode="true"
                :id="item.raw.id"
                :initials="makeInitials(item.raw)"
              />
            </v-col>
            <v-col
              class="group-details"
              cols="auto"
            >
              <span class="font-weight-bold">{{ item.raw.name }}</span>
            </v-col>
          </v-row>
        </v-list-item-title>
        <template #append>
          <div v-if="loadItemChips.length > 0">
            <dictionary-item-chip
              v-for="element in loadItemChips(item.raw)"
              :key="element.id"
              :element="element"
              v-bind="$attrs"
              variant="flat"
            />
          </div>
        </template>
      </v-list-item>
    </template>

    <template #no-data>
      <v-list-item v-if="loading">
        <v-progress-linear
          color="primary"
          indeterminate
        ></v-progress-linear>
      </v-list-item>
      <v-list-item
        v-else
        :title="t('dictionary.noData')"
      />
    </template>
  </dictionary-base>
</template>

<script lang="ts" setup>
import { useEventBus } from '@vueuse/core';
import { debounce } from 'lodash';

import { computed, onMounted, ref, toRef, watch } from 'vue';

import DictionaryBase from '@/components/controls/dictionary/DictionaryBase.vue';
import DictionaryItemChip from '@/components/controls/dictionary/DictionaryItemChip.vue';
import AvatarProvider from '@/components/controls/user-input/AvatarProvider.vue';

import {
  useClass,
  useDictionary,
  useFormModel,
  useLabel,
  useLocale,
  useProps,
  useResolveVariables,
  useRules,
} from '@/core/composables';
import { useActiveRules } from '@/core/composables/useActiveRules';
import { useEventHandler } from '@/core/composables/useEventHandler';
import { variableRegexp } from '@/core/engine/utils';
import { logger } from '@/main';
import { User } from '@/types/engine/User';
import { EngineDictionaryField, EngineGroupField } from '@/types/engine/controls';

const props = defineProps<{
  schema: EngineGroupField;
  model: object;
  validationsDisabled: boolean;
}>();

const { t } = useLocale();
const { bindClass } = useClass();
const { bindRules, rules, requiredInputClass } = useRules();
const { bindProps, fieldProps } = useProps();
const { onChange } = useEventHandler();
const { label, bindLabel } = useLabel(props.schema);
const { getValue, setValue } = useFormModel();
const { resolve } = useResolveVariables();

const {
  multiple,
  maxSelection,
  queryBlocker,
  loading,
  data,
  query,
  paginationOptions,
  load,
  loadMoreRecords,
  singleOptionAutoSelect,
  initState,
  loadCounter,
  dependencyWasChanged,
  loadItemChips,
} = useDictionary();

const debounced = {
  load: debounce(load, 300),
};

const { activeRules } = useActiveRules({
  fieldProps,
  validationsDisabled: toRef(() => props.validationsDisabled),
  rules,
});

const localModel = computed({
  get(): User | User[] | undefined | null {
    return getValue(props.model, props.schema);
  },
  set(val: any) {
    if (typeof val !== 'string') {
      setValue(val, props.schema);
      query.value = '';
    }
  },
});

function valueHasChanged(val: any) {
  onChange(props.schema, props.model); // fire events if defined
}

// DEFAULTS VALUES //
const DEFAULT_URL = ref<string>('/api/v1/groups');

const menu = ref(false);
const showMenuItemsOnFocusIn = props.schema.source.showMenuItemsOnFocusIn
  ? props.schema.source.showMenuItemsOnFocusIn
  : false;

async function fetchData(event: any) {
  if (!fieldProps.value.readonly) {
    await load('load');
  }
  if (showMenuItemsOnFocusIn) {
    event.target.click();
  }
}

async function checkIfURLHasDependency(createListener = false) {
  const isApiContainsDependency = !(DEFAULT_URL.value.match(variableRegexp) == null);
  if (isApiContainsDependency) {
    let endpoint = await resolve(props.schema, DEFAULT_URL.value, true);

    if (endpoint.resolvedText.match(variableRegexp)) {
      endpoint = await resolve(props.schema, endpoint.resolvedText, true);
    }

    if (endpoint.allVariablesResolved) {
      DEFAULT_URL.value = endpoint.resolvedText;
      await load('deps');
    } else if (logger.dictionaryLogger) {
      console.debug(
        `[vue-schema-forms] [DictionaryLogger] => API call was blocked, not every variable from endpoint was resolved ${DEFAULT_URL.value}`,
      );
    }

    if (createListener) {
      const vueSchemaFormEventBus = useEventBus<string>('form-model');
      const unsubscribe = vueSchemaFormEventBus.on(async () => await listener());

      const listener = async () => {
        await new Promise((r) => setTimeout(r, 50));
        const temp = await resolve(props.schema, props.schema.source.url as string, true);
        if (temp.resolvedText !== DEFAULT_URL.value) {
          DEFAULT_URL.value = temp.resolvedText;
          await load('listener');
        }
      };
    }
  }
}

function removeValue(item: User) {
  if (fieldProps.value.multiple) {
    const tempArray = (localModel.value as User[]).filter((val) => val != item);
    localModel.value = tempArray.length > 0 ? tempArray : null;
  } else {
    localModel.value = null;
  }
}

function makeInitials(item: any): string {
  const { firstName, lastName, email } = item;

  const firstInitial =
    typeof firstName === 'string' && firstName.length > 0 ? firstName.charAt(0).toUpperCase() : '';

  const lastInitial =
    typeof lastName === 'string' && lastName.length > 0 ? lastName.charAt(0).toUpperCase() : '';

  if (firstInitial || lastInitial) {
    return firstInitial + lastInitial;
  }

  if (typeof email === 'string' && email.length >= 2) {
    return email.charAt(0).toUpperCase() + email.charAt(1).toUpperCase();
  }

  return 'U';
}

function singleOptionAutoSelectFunction() {
  const selectSingleOptionLogic = () => {
    if (data.value.length !== 1 || !singleOptionAutoSelect.value || loadCounter.value > 1) return;
    const selectedValue = data.value[0];

    if (JSON.stringify(localModel.value) !== JSON.stringify(selectedValue)) {
      if (fieldProps.value.multiple) {
        localModel.value = [selectedValue];
      } else {
        localModel.value = selectedValue;
      }
    }
  };

  selectSingleOptionLogic();
  watch(data, selectSingleOptionLogic, { deep: true });
}

onMounted(async () => {
  if (props.schema.source.url == '' || props.schema.source.url == null) {
    props.schema.source.url = DEFAULT_URL.value;
  }

  await initState(props.schema as EngineDictionaryField);

  await bindLabel(props.schema);
  await bindRules(props.schema);
  await bindProps(props.schema);

  await checkIfURLHasDependency(true);
  singleOptionAutoSelectFunction();
});
</script>

<style lang="css" scoped></style>
